import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import '../styles/VoiceAliasTab.css';

const VoiceAliasTab = ({ itemId, itemType, itemName }) => {
  const [userAliases, setUserAliases] = useState([]);
  const [systemAliases, setSystemAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newAlias, setNewAlias] = useState('');
  const [testTerm, setTestTerm] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (itemId && itemType) {
      fetchAliases();
    }
  }, [itemId, itemType]);

  const fetchAliases = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get all aliases for this item (both system and user)
      const response = await fetch(`${API_URL}/api/user/items-with-aliases`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Find this specific item
        const item = data.items.find(
          i => String(i.item_id) === String(itemId) && i.item_type === itemType
        );
        
        if (item && item.aliases) {
          // Separate user and system aliases
          const userAls = item.aliases.filter(a => a.is_user_alias === 1);
          const systemAls = item.aliases.filter(a => a.is_user_alias === 0);
          setUserAliases(userAls);
          setSystemAliases(systemAls);
        } else {
          setUserAliases([]);
          setSystemAliases([]);
        }
      } else {
        throw new Error('Failed to fetch aliases');
      }
    } catch (err) {
      setError('Failed to load voice aliases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlias = async () => {
    if (!newAlias.trim()) {
      setError('Please enter an alias');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const response = await fetch(`${API_URL}/api/user/voice-aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          alias: newAlias.trim(),
          item_id: itemId,
          item_type: itemType
        })
      });

      if (response.ok) {
        await fetchAliases();
        setNewAlias('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add alias');
      }
    } catch (err) {
      setError('Failed to add voice alias');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/voice-aliases/${aliasId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchAliases();
      } else {
        throw new Error('Failed to delete alias');
      }
    } catch (err) {
      setError('Failed to delete voice alias');
      console.error(err);
    }
  };

  const handleTestVoice = async () => {
    if (!testTerm.trim()) {
      setError('Please enter a test phrase');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/test-voice-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ searchTerm: testTerm })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
      } else {
        throw new Error('Test failed');
      }
    } catch (err) {
      setError('Failed to test voice search');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="voice-alias-tab">
      <div className="mb-4">
        <h5 className="mb-3">Voice Commands for "{itemName}"</h5>
        <p className="text-muted small">
          Add custom voice commands that Alexa will recognize when you want to add this item to your shopping list.
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Current Aliases */}
      <div className="mb-4">
        <h6>Your Custom Voice Aliases:</h6>
        {userAliases.length === 0 ? (
          <p className="text-muted small">No custom voice aliases yet. Add one below!</p>
        ) : (
          <ul className="list-group mb-3">
            {userAliases.map(alias => (
              <li key={alias.id} className="list-group-item d-flex justify-content-between align-items-center">
                <span className="font-monospace">"{alias.alias}"</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteAlias(alias.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </li>
            ))}
          </ul>
        )}
        
        {systemAliases.length > 0 && (
          <>
            <h6 className="mt-3">System-Generated Aliases:</h6>
            <p className="text-muted small">These are automatically generated based on common patterns</p>
            <ul className="list-group">
              {systemAliases.map((alias, idx) => (
                <li key={`system-${idx}`} className="list-group-item">
                  <span className="font-monospace text-secondary">"{alias.alias}"</span>
                  <span className="badge bg-secondary ms-2">System</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Add New Alias */}
      <div className="mb-4">
        <h6>Add New Voice Alias:</h6>
        <div className="d-flex gap-2">
          <Form.Control
            type="text"
            placeholder='e.g., "greek yogurt" for "Greek Yogurt, 0% fat"'
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddAlias()}
          />
          <Button 
            variant="primary" 
            onClick={handleAddAlias}
            disabled={saving || !newAlias.trim()}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add
          </Button>
        </div>
        <Form.Text className="text-muted">
          This is what you'll say to Alexa to add this item
        </Form.Text>
      </div>

      {/* Test Voice Recognition */}
      <div className="mb-3">
        <h6>Test Voice Recognition:</h6>
        <div className="d-flex gap-2 mb-2">
          <Form.Control
            type="text"
            placeholder="Test a voice command..."
            value={testTerm}
            onChange={(e) => setTestTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleTestVoice()}
          />
          <Button 
            variant="outline-primary" 
            onClick={handleTestVoice}
            disabled={!testTerm.trim()}
          >
            <FontAwesomeIcon icon={faVolumeUp} className="me-1" />
            Test
          </Button>
        </div>
        
        {testResult && (
          <div className="alert alert-info mt-2">
            <strong>Results for "{testResult.searchTerm}":</strong>
            {testResult.results.length === 0 ? (
              <p className="mb-0 mt-2">No matches found</p>
            ) : (
              <ol className="mb-0 mt-2">
                {testResult.results.slice(0, 3).map((result, idx) => (
                  <li key={idx}>
                    {result.name} 
                    {result.name === itemName && <span className="text-success ms-2">(This item)</span>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      <div className="alert alert-info mb-0">
        <small>
          <strong>Tip:</strong> Keep aliases simple and natural. For "Fire Roasted Diced Tomatoes", 
          try aliases like "diced tomatoes" or just "tomatoes".
        </small>
      </div>
    </div>
  );
};

export default VoiceAliasTab;